import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { StyledCheckbox } from '../../Common/MUIStyledComponents';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Popper from '@mui/material/Popper';

const PREFIX = 'CustomAutocomplete';

const classes = {
  formControl: `${PREFIX}-formControl`,
  chips: `${PREFIX}-chips`,
  chip: `${PREFIX}-chip`
};

const StyledFormControl = styled(FormControl)(() =>
  ({
    [`&.${classes.formControl}`]: {
      width: '100%',
      borderRadius: '8px',
      display: 'flex',
      justifyContent: 'flex-start',
      height: '100%'
    },
  }));

interface IMultiProps {
  options: any[];
  placeholder: string;
  onChange: any;
  toClear: boolean;
  selections: any;
  countOpts?: string[];
  agOpts?: string[];
  disable: boolean;
}

const FilterScreeningAutocomplete: React.FC<IMultiProps> = ({ selections, options, onChange, toClear, placeholder, countOpts, agOpts, disable }) => {
  const [ selected, setSelected ] = React.useState<any | null>([]);

  const mapFromOpts = new Map(
    options.map((opt: any) => [ opt.key, opt ])
  );

  const uniqueOptions = [ ...mapFromOpts.values() ];

  const sortedOptions = uniqueOptions.sort((a, b) => {
    if (selected.length > 0) {
      return selected.indexOf(a.key) > -1 ? -1 : 1;
    }
    if (agOpts) {
      return agOpts.indexOf(a.key) > -1 ? -1 : 1;
    }
    if (countOpts) {
      return countOpts.indexOf(a.key) > -1 ? -1 : 1;
    }
    if (selected.indexOf(a.key) > -1 === selected.indexOf(b.key) > -1) {
      return a.key > b.key ? 1 : -1;
    }
    return selected.indexOf(a.key) > -1 ? -1 : 1;
  });

  const handleChange = (event: any, newValue: any | null, reason: any) => {
    if (reason === 'selectOption') {
      let valueToAdd = newValue.map((option: any) => option.key || option);
      valueToAdd = valueToAdd[valueToAdd.length - 1];
      setSelected([ ...selected, valueToAdd ]);
      onChange([ ...selected, valueToAdd ]);
    }
    if (reason === 'removeOption') {
      setSelected(newValue);
      onChange(newValue);
    }
  };

  useEffect(() => {
    setSelected(selections);
  }, [ selections ]);

  useEffect(() => {
    if (toClear) {
      setSelected([]);
      onChange([]);
    }
  }, [ toClear ]);

  const CustomPopper = (props: any) => {
    return (
      <Popper
        {...props}
        modifiers={[
          {
            name: 'preventOverflow',
            options: { boundary: 'clippingParents' }, // Keeps dropdown inside parent div
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: [ 'bottom', 'top' ], // Avoids dropdown going out of view
            },
          },
        ]}
        style={{ zIndex: 1300 }} // Ensures it stays above other elements
        disablePortal
      />
    );
  };

  return (
    <StyledFormControl className={classes.formControl} style={{ position: 'relative' }}>
      <Autocomplete
        value={selected}
        multiple
        onChange={(e: any, newValue: any | null, reason: any) => handleChange(e, newValue, reason)}
        options={sortedOptions}
        open={true}
        disablePortal
        PopperComponent={CustomPopper}
        noOptionsText={'No options available'}
        getOptionLabel={(option) => option.key}
        isOptionEqualToValue={(option, value) => option.key === value}
        renderOption={(props, opt) => (
          <MenuItem style={{ whiteSpace: 'pre-wrap' }}
            value={opt.key}
            {...props}
            disabled={selected.indexOf(opt.key) === -1 && ((agOpts && disable && agOpts.indexOf(opt.key) === -1) || (countOpts && disable && countOpts.indexOf(opt.key) === -1))}
          >
            <StyledCheckbox
              className='filter-checkbox'
              checked={selected.indexOf(opt.key) > -1}
            />
            <ListItemText primary={opt.key} />
          </MenuItem>
        )}
        popupIcon={(
          <div></div>
        )}
        renderTags={() => {
          return (
            <></>
          );
        }
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
          />
        )
        }
      />
    </StyledFormControl>
  );
};

export default FilterScreeningAutocomplete;
